/**
 * One survey, as a card inside its BU's section.
 *
 * Every counter on this card belongs to **this** survey. Nothing here is ever summed with the
 * card next to it: a 1–5 rating about the menu and a 0–10 NPS about maps share neither scale nor
 * population, so a total across them would be a number that reads as information and is not
 * (R21). That is also why the response rate is fetched per card, from that survey's own results,
 * instead of being derived from anything the listing knows.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import { useSurveyResults } from '../../Hooks/useSurveyResults';
import type { Survey } from '../../Types/Surveys.types';
import {
  APPROVAL_LABELS,
  APPROVAL_TONES,
  EXIT_STATE_CONSEQUENCES,
  EXIT_STATE_LABELS,
  EXIT_STATE_TONES,
  LAYOUT_LABELS,
  TRIGGER_ICONS,
  TRIGGER_LABELS,
  exitSurveyState,
  formatCount,
  formatWindow,
  isExitSurvey,
  isWithinWindow,
  responseRateLabel,
  surveyTitle,
} from '../../Utils/surveyFormat';
import { IconButton } from '@/Shared/components/IconButton/IconButton';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding: var(--space-ml);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  background: var(--color-surface);
  transition: box-shadow var(--duration-fast) var(--ease-standard);

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const headerRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const slugStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-secondary);
`;

const spacerStyle = css`
  flex: 1;
`;

const identityStyle = css`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const titleStyle = css`
  font-size: 14px;
  font-weight: var(--font-weight-semibold);
  text-wrap: pretty;
`;

const metaStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px var(--space-s);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

/**
 * The trigger, with its icon, as one unit.
 *
 * `inline-flex` so the icon and the words never break apart when the line wraps — a bell landing
 * alone at the end of a row would be the legend-less riddle the icon is chosen not to be. The
 * colour is a shade stronger than the rest of the meta line: among four grey fragments, the one
 * that says *when this thing happens* is the one worth finding first.
 */
const triggerStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--color-text-secondary);
`;

const countersStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-s);
  padding: var(--space-s) var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
`;

const counterStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const counterValueStyle = css`
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
`;

const counterLabelStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  padding-top: var(--space-s);
  border-top: 1px solid var(--color-divider);
  margin-top: auto;
`;

/**
 * Deleted rows read as switched off, not as broken.
 *
 * Dashed border and faded content say "this is not in play"; the content stays legible, because
 * reading the slug it is still holding is the whole reason it is on screen.
 */
const deletedCardStyle = css`
  border-style: dashed;
  background: var(--color-surface-hover);

  &:hover {
    box-shadow: none;
  }
`;

const fadedStyle = css`
  opacity: 0.6;
`;

const deletedNoteStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

/** `2026-09-22T15:06:25.172Z` → `22/09/2026 15:06`. Invalid or absent dates print nothing. */
const formatDeletedAt = (value: string | null | undefined): string => {
  if (!value) return '';
  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

export interface SurveyCardProps {
  survey: Survey;
  canEdit: boolean;
  canDelete: boolean;
  canReadResults: boolean;
  onResults: (survey: Survey) => void;
  onEdit: (survey: Survey) => void;
  onDelete: (survey: Survey) => void;
  /** Absent when restoring is not on offer — the button then is not rendered at all. */
  onRestore?: (survey: Survey) => void;
  isRestoring?: boolean;
}

export const SurveyCard = ({
  survey,
  canEdit,
  canDelete,
  canReadResults,
  onResults,
  onEdit,
  onDelete,
  onRestore,
  isRestoring = false,
}: SurveyCardProps): JSX.Element => {
  const isDeleted = Boolean(survey.deletedAt);
  const deletedAt = formatDeletedAt(survey.deletedAt);
  /* The pt row when there is one; the English base column only when there is not. The aria-labels
     use the same string, or a screen reader would announce a name nobody can see on the card. */
  const title = surveyTitle(survey);

  /**
   * The rate, asked for only when there is something to divide.
   *
   * The listing carries `responseCount` but no denominator — the eligible population is a union
   * computed per survey (R22) — so the only honest source is that survey's own results endpoint.
   * A survey with no answers never asks.
   */
  const { data: results } = useSurveyResults(
    survey.id,
    undefined,
    canReadResults && survey.responseCount > 0 && !isDeleted,
  );

  const live =
    !isDeleted && survey.isActive && survey.approvalStatus === 'APPROVED' && isWithinWindow(survey);

  /**
   * The exit questionnaire is not "no ar": it is attached, or it is orphaned.
   *
   * It is never delivered on its own (R34), so a badge saying it is on the air would describe a
   * delivery that does not happen. What matters instead is whether the feature can reach it — and
   * an orphan has to be **visible**, because it means a question was written and nobody is being
   * asked, with nothing anywhere raising an error.
   */
  const isExit = isExitSurvey(survey);
  const exitState = isExit ? exitSurveyState(survey) : null;

  return (
    <article
      id={`survey-card-${survey.id}`}
      className={`${cardStyle} ${isDeleted ? deletedCardStyle : ''}`}
    >
      <div className={headerRowStyle}>
        <span className={slugStyle}>{survey.slug}</span>
        <div className={spacerStyle} />
        {isDeleted ? (
          <StatusTag id={`survey-deleted-${survey.id}`} status='danger' size='sm' dot>
            {deletedAt ? `Excluída em ${deletedAt}` : 'Excluída'}
          </StatusTag>
        ) : null}
        <StatusTag
          id={`survey-status-${survey.id}`}
          status={APPROVAL_TONES[survey.approvalStatus]}
          size='sm'
          dot
        >
          {APPROVAL_LABELS[survey.approvalStatus]}
        </StatusTag>
        {isExit ? (
          exitState === 'READY' || exitState === 'ORPHAN' ? (
            <StatusTag
              id={`survey-exit-state-${survey.id}`}
              status={EXIT_STATE_TONES[exitState]}
              size='sm'
              dot
            >
              {EXIT_STATE_LABELS[exitState]}
            </StatusTag>
          ) : null
        ) : live ? (
          <StatusTag id={`survey-live-${survey.id}`} status='info' size='sm' dot>
            No ar
          </StatusTag>
        ) : null}
        {survey.draftVersion !== null ? (
          <StatusTag id={`survey-draft-${survey.id}`} status='pending' size='sm' dot>
            v{survey.draftVersion} em rascunho
          </StatusTag>
        ) : null}
      </div>

      <div className={`${identityStyle} ${isDeleted ? fadedStyle : ''}`}>
        <span className={titleStyle}>{title}</span>
        {/*
          Delivery has nothing to say about an exit questionnaire — it has no queue, no priority and
          no audience of its own — so its line names the moment it is asked instead of a window it
          does not use.
        */}
        <div className={metaStyle}>
          <span className={triggerStyle}>
            <GLIcon
              variant={TRIGGER_ICONS[survey.trigger]}
              size={13}
              fill='var(--color-text-secondary)'
            />
            {TRIGGER_LABELS[survey.trigger]}
          </span>
          <span>·</span>
          {isExit ? null : (
            <>
              <span>{LAYOUT_LABELS[survey.layout]}</span>
              <span>·</span>
            </>
          )}
          <span>v{survey.version} no ar</span>
        </div>
        <div className={metaStyle}>
          <span>
            {isExit
              ? (survey.requiresOptInFeature?.name ?? 'Nenhuma funcionalidade vinculada')
              : formatWindow(survey.opensAt, survey.closesAt)}
          </span>
        </div>
      </div>

      {/*
        R35 in the listing: a questionnaire that is not served fails in silence everywhere else —
        no error, no empty state, just a modal that stops asking. The card is one of the two places
        that can say so.
      */}
      {!isDeleted && exitState && exitState !== 'READY' ? (
        <p id={`survey-exit-warning-${survey.id}`} className={deletedNoteStyle}>
          {EXIT_STATE_CONSEQUENCES[exitState]}
        </p>
      ) : null}

      {isDeleted ? (
        <p className={deletedNoteStyle}>
          Continua segurando o slug <strong>{survey.slug}</strong>, então nenhuma pesquisa nova pode
          usá-lo. Restaurar traz de volta o questionário e as respostas —{' '}
          <strong>desativada</strong>, porque restaurar não põe pesquisa no ar.
        </p>
      ) : null}

      <div className={`${countersStyle} ${isDeleted ? fadedStyle : ''}`}>
        <div className={counterStyle}>
          <span id={`survey-responses-${survey.id}`} className={counterValueStyle}>
            {formatCount(survey.responseCount)}
          </span>
          <span className={counterLabelStyle}>respostas desta pesquisa</span>
        </div>
        <div className={counterStyle}>
          <span id={`survey-rate-${survey.id}`} className={counterValueStyle}>
            {results ? responseRateLabel(results.eligibilityScope, results.responseRate) : '—'}
          </span>
          <span className={counterLabelStyle}>
            {results?.eligibilityScope === 'EVERYONE'
              ? 'sem denominador: alcança todo o tenant'
              : 'taxa de resposta'}
          </span>
        </div>
      </div>

      <div className={footerStyle}>
        {isDeleted ? (
          <>
            <div className={spacerStyle} />
            {onRestore ? (
              <UTButton
                fitContent
                variant='secondary'
                id={`survey-restore-${survey.id}`}
                text='Restaurar'
                icon='Arrows.ArrowCounterClockwise'
                loading={isRestoring}
                onClick={() => {
                  onRestore(survey);
                }}
              />
            ) : null}
          </>
        ) : (
          <>
            {canReadResults ? (
              <UTButton
                fitContent
                variant='secondary'
                id={`survey-results-${survey.id}`}
                text='Ver resultados'
                icon='Misc.ChartLineUp'
                onClick={() => {
                  onResults(survey);
                }}
              />
            ) : null}
            <div className={spacerStyle} />
            {canEdit ? (
              <IconButton
                id={`survey-edit-${survey.id}`}
                icon='Settings.PencilSimple'
                aria-label={`Editar ${title}`}
                onClick={() => {
                  onEdit(survey);
                }}
              />
            ) : null}
            {canDelete ? (
              <IconButton
                id={`survey-delete-${survey.id}`}
                icon='Misc.Trash'
                aria-label={`Excluir ${title}`}
                variant='danger'
                onClick={() => {
                  onDelete(survey);
                }}
              />
            ) : null}
          </>
        )}
      </div>
    </article>
  );
};
